import { Link, useNavigate } from "react-router-dom";
import {
  Search, Undo2, Download, Share2, Settings2, ChevronDown, FileImage, FileText,
  Lock, UserPlus, FileUp, ArrowLeft,
} from "lucide-react";

export function ChartToolbar({
  view, viewId, views, query, setQuery, results, onPickResult, activeCount, layout,
  onExpandAll, onCollapseLevel2, canReparent, onUndo,
  exporting, exportMenu, setExportMenu, onExport, onAddPerson, onOpenSettings, onOpenShare,
}) {
  const navigate = useNavigate();
  return (
    <div className="h-12 shrink-0 bg-white border-b border-[#181B34]/10 flex items-center gap-2 px-4 z-30">
      <Link to="/" data-testid="back-to-gallery" title="All charts" className="w-8 h-8 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><ArrowLeft size={16} /></Link>
      <div className="relative">
        <select data-testid="view-selector" value={viewId} onChange={(e) => navigate(`/chart/${e.target.value}`)}
          className="h-8 pl-3 pr-8 rounded-lg bg-[#F0F3FF] font-bold text-sm appearance-none cursor-pointer focus:outline-none max-w-56 truncate">
          {views.map((v) => <option key={v.id} value={v.id}>{v.name}{v.is_published ? "" : " (draft)"} — {v.created_by_name || "Admin"}</option>)}
        </select>
        <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#676a82]" />
      </div>
      {view.can_edit ? (
        <>
          <button data-testid="add-person-btn" onClick={onAddPerson} title="Add person to this chart"
            className="flex items-center gap-1 px-2.5 h-8 rounded-lg bg-[#00C875]/12 text-[#00925a] text-xs font-bold hover:bg-[#00C875]/25">
            <UserPlus size={14} /> Add person
          </button>
          <button data-testid="import-to-chart-btn" onClick={() => navigate(`/imports?chart=${view.id}`)} title="Import into this chart"
            className="w-8 h-8 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><FileUp size={15} /></button>
          <button data-testid="view-settings-btn" title="Chart settings" onClick={onOpenSettings} className="w-8 h-8 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><Settings2 size={16} /></button>
          <button data-testid="share-view-btn" title="Share" onClick={onOpenShare} className="w-8 h-8 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><Share2 size={15} /></button>
        </>
      ) : (
        <span data-testid="view-locked-badge" title={`Created by ${view.created_by_name || "an admin"} — ask them for co-author access to edit`}
          className="flex items-center gap-1 px-2 h-7 rounded-full bg-[#181B34]/6 text-[10px] font-bold text-[#676a82]">
          <Lock size={10} /> {view.created_by_name ? `by ${view.created_by_name}` : "read-only"}
        </span>
      )}
      <div className="relative ml-2 flex-1 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
        <input data-testid="chart-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search this chart…"
          className="w-full h-8 pl-8 pr-3 rounded-lg bg-[#F6F7FB] text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6161FF]/30" />
        {results.length > 0 && (
          <div className="absolute top-9 left-0 right-0 bg-white rounded-xl border border-[#181B34]/10 shadow-xl overflow-hidden z-50">
            {results.map((r) => (
              <button key={r.id} data-testid={`search-result-${r.employee_id}`}
                onClick={() => onPickResult(r)}
                className="w-full text-left px-3 py-2 hover:bg-[#F0F3FF] flex items-center gap-2">
                <span className="font-bold text-sm">{r.full_name}</span>
                <span className="text-xs text-[#676a82]">{r.designation}</span>
                <span className="ml-auto text-[10px] font-mono text-[#9699AD]">{r.employee_id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="ml-auto flex items-center gap-1">
        <span className="text-xs font-bold text-[#9699AD] px-2" data-testid="chart-headcount">{activeCount} people</span>
        {(layout === "classic" || layout === "horizontal") && activeCount > 0 && (
          <>
            <button data-testid="expand-all-btn" onClick={onExpandAll} className="px-2.5 h-8 rounded-lg hover:bg-[#F0F3FF] text-xs font-bold text-[#4b4e63]">Expand all</button>
            <button data-testid="collapse-l2-btn" onClick={onCollapseLevel2} className="px-2.5 h-8 rounded-lg hover:bg-[#F0F3FF] text-xs font-bold text-[#4b4e63]">Level 2</button>
          </>
        )}
        {canReparent && (
          <button data-testid="undo-btn" onClick={onUndo} title="Undo last move (Ctrl+Z)" className="w-8 h-8 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><Undo2 size={16} /></button>
        )}
        <div className="relative">
          <button data-testid="export-btn" onClick={() => setExportMenu(!exportMenu)} disabled={exporting || activeCount === 0}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-[#181B34] text-white text-xs font-bold disabled:opacity-50">
            <Download size={13} /> {exporting ? "Exporting…" : "Export"}
          </button>
          {exportMenu && (
            <div className="absolute right-0 top-9 bg-white rounded-xl border border-[#181B34]/10 shadow-xl overflow-hidden z-50 w-36">
              <button data-testid="export-png-btn" onClick={() => onExport("png")} className="w-full px-3 py-2 text-left text-sm font-bold hover:bg-[#F0F3FF] flex items-center gap-2"><FileImage size={14} /> PNG</button>
              <button data-testid="export-pdf-btn" onClick={() => onExport("pdf")} className="w-full px-3 py-2 text-left text-sm font-bold hover:bg-[#F0F3FF] flex items-center gap-2"><FileText size={14} /> PDF</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
